import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { toShareableUrl } from './siteUrl';

/**
 * Share a link via the native share sheet on supported platforms,
 * falling back to clipboard copy. Returns { method } indicating which path ran.
 *
 * Web: navigator.share → navigator.clipboard (unchanged).
 * Native (Capacitor): @capacitor/share → @capacitor/clipboard.
 *
 * Share URLs are always canonical https (prii.app / VITE_SITE_URL) — never capacitor://.
 *
 * With `text`, the line and the link go out as ONE message ("Join my group for X\nhttps://prii.app/…") rather than
 * as separate text and url fields: Messages and other targets were keeping the link and dropping the text (operator
 * QA, 2026-09-29). The link on its own last line is what Messages turns into the preview card (api/og-card.ts).
 * No `title` goes with a message: iOS adds it as its own first line, so "Mac DeMarco" showed twice (QA, same day).
 *
 * @param {Object} opts
 * @param {string} opts.url - Full URL or app path (/events/id, /groups/id, …).
 * @param {string} [opts.title] - Title hint for the share sheet (some apps use it).
 * @param {string} [opts.text] - Body text hint for the share sheet.
 * @returns {Promise<{ method: 'native' | 'clipboard' | 'cancelled' | 'failed' }>}
 */
export async function shareLink({ url, title, text }) {
  if (!url) throw new Error('shareLink: url is required');

  const shareUrl = toShareableUrl(url);
  const message = text ? `${text}\n${shareUrl}` : null;

  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share(
        message
          ? { text: message, dialogTitle: title || 'Share' }
          : { url: shareUrl, title, dialogTitle: title || 'Share' },
      );
      return { method: 'native' };
    } catch (err) {
      if (isShareCancelled(err)) {
        return { method: 'cancelled' };
      }
      logShareError('native Share.share', err);
      // Fall through to native clipboard below.
    }
  } else if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share(message ? { text: message } : { url: shareUrl, title });
      return { method: 'native' };
    } catch (err) {
      if (isShareCancelled(err)) {
        return { method: 'cancelled' };
      }
      logShareError('navigator.share', err);
      // Fall through to web clipboard below.
    }
  }

  try {
    await copyTextToClipboard(shareUrl);
    return { method: 'clipboard' };
  } catch (err) {
    logShareError('clipboard', err);
    return { method: 'failed' };
  }
}

/**
 * Copy plain text to the clipboard (addresses, URLs, etc.).
 * Uses @capacitor/clipboard on native; navigator.clipboard on web.
 */
export async function copyTextToClipboard(text) {
  if (text == null || text === '') {
    throw new Error('copyTextToClipboard: text is required');
  }
  if (Capacitor.isNativePlatform()) {
    await Clipboard.write({ string: String(text) });
    return;
  }
  await navigator.clipboard.writeText(String(text));
}

function isShareCancelled(err) {
  if (!err) return false;
  if (err.name === 'AbortError') return true;
  const message = String(err.message ?? err.errorMessage ?? err).toLowerCase();
  return (
    message.includes('share canceled') ||
    message.includes('share cancelled') ||
    message.includes('cancelled') ||
    message.includes('canceled') ||
    message === 'cancel'
  );
}

function logShareError(phase, err) {
  if (isShareCancelled(err)) return;
  const name = err?.name || 'Error';
  const message = err?.message ?? err?.errorMessage ?? String(err);
  console.error(`[shareLink] ${phase} failed:`, name, message, err);
}
